import { CartAddEvent } from '@theme/events';

const MAX_TITLE_LENGTH = 40;

class WifiFrequentlyBoughtTogether extends HTMLElement {
  /** @type {Map<string, {variantId: string, price: number, title: string}>} */
  #selections = new Map();

  connectedCallback() {
    this.list = this.querySelector('[data-wifi-fbt-list]');
    this.submitButton = this.querySelector('[data-wifi-fbt-submit]');
    this.countEl = this.querySelector('[data-wifi-fbt-count]');
    this.totalEl = this.querySelector('[data-wifi-fbt-total-price]');

    this.submitButton?.addEventListener('click', this.#handleSubmit);
    this.#load();
  }

  async #load() {
    const productId = this.dataset.productId;
    const limit = this.dataset.limit || '2';
    const intent = this.dataset.intent || 'complementary';

    let products = [];

    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`
      );
      if (response.ok) {
        const data = await response.json();
        products = data.products || [];
      }
    } catch {
      /* network error — fall through to collection fallback */
    }

    if (products.length === 0) {
      const collectionHandle = this.dataset.collectionHandle;
      if (collectionHandle) {
        try {
          const response = await fetch(`/collections/${collectionHandle}/products.json?limit=${Number(limit) + 1}`);
          if (response.ok) {
            const data = await response.json();
            products = (data.products || []).filter((product) => String(product.id) !== productId);
          }
        } catch {
          /* no fallback available either — section stays hidden */
        }
      }
    }

    if (products.length === 0) return;

    this.#render(products.slice(0, Number(limit)));
    this.removeAttribute('hidden');
  }

  /**
   * @param {Array<object>} products
   */
  #render(products) {
    if (!this.list) return;

    const fragments = products.map((product) => {
      const variant = product.variants?.[0];
      if (!variant) return '';

      const price = variant.price ? Number(variant.price) : 0;
      const key = `product-${product.id}`;
      this.#selections.set(key, {
        variantId: String(variant.id),
        price,
        title: product.title,
      });

      const truncatedTitle =
        product.title.length > MAX_TITLE_LENGTH ? `${product.title.slice(0, MAX_TITLE_LENGTH)}…` : product.title;
      const image = product.images?.[0]?.src || product.featured_image || '';
      const priceFormatted = this.#formatMoney(price);

      return `
        <span class="wifi-fbt__plus" aria-hidden="true">+</span>
        <label class="wifi-fbt-item">
          <input type="checkbox" class="wifi-fbt-item__checkbox" data-wifi-fbt-checkbox data-key="${key}" checked>
          ${image ? `<img class="wifi-fbt-item__image" src="${image}" alt="" loading="lazy" width="56" height="56">` : ''}
          <span class="wifi-fbt-item__body">
            <span class="wifi-fbt-item__title">${truncatedTitle}</span>
            <span class="wifi-fbt-item__price" data-price="${price}">${priceFormatted}</span>
          </span>
        </label>
      `;
    });

    this.list.insertAdjacentHTML('beforeend', fragments.join(''));

    this.querySelectorAll('[data-wifi-fbt-checkbox]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.#updateSummary());
    });

    this.#updateSummary();
  }

  #updateSummary() {
    let count = 0;
    let total = 0;

    this.querySelectorAll('[data-wifi-fbt-checkbox]').forEach((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;
      const selection = this.#selections.get(checkbox.dataset.key || '');
      if (!selection) return;
      count += 1;
      total += selection.price;
    });

    if (this.countEl) {
      this.countEl.textContent = String(count);
    }
    if (this.totalEl) {
      this.totalEl.textContent = this.#formatMoney(total);
    }
    if (this.submitButton instanceof HTMLButtonElement) {
      this.submitButton.disabled = count === 0;
    }
  }

  #handleSubmit = async () => {
    const items = [];

    this.querySelectorAll('[data-wifi-fbt-checkbox]').forEach((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;
      const selection = this.#selections.get(checkbox.dataset.key || '');
      if (!selection) return;
      items.push({ id: selection.variantId, quantity: 1 });
    });

    if (items.length === 0 || !(this.submitButton instanceof HTMLButtonElement)) return;

    this.submitButton.disabled = true;
    const originalText = this.submitButton.textContent;
    this.submitButton.textContent = '…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (response.ok) {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        document.dispatchEvent(new CartAddEvent(cart, this.id || 'wifi-fbt', { itemCount: cart.item_count }));
      }
    } catch {
      /* fail silently — button re-enables below so the shopper can retry */
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.textContent = originalText;
    }
  };

  /**
   * @param {number} amount - decimal currency amount, e.g. 24.99
   */
  #formatMoney(amount) {
    const currency = this.dataset.currency || 'USD';
    const locale = this.dataset.locale || document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }
}

if (!customElements.get('wifi-frequently-bought-together')) {
  customElements.define('wifi-frequently-bought-together', WifiFrequentlyBoughtTogether);
}
